import type { z } from 'zod';

import {
  IDEMPOTENCY_KEY_HEADER,
  IdempotencyKeySchema,
  parseProblem,
  type Problem,
  type ReasonCode,
} from '../../api';

/**
 * A failed API call, reduced to what the screen may use: the HTTP status, the stable
 * `reasonCode` and the `params` its text interpolates. Screens show
 * `reasonMessage(reasonCode, params)` texts only, never a raw message (I9, I12).
 */
export class ApiError extends Error {
  readonly status: number;
  readonly reasonCode: ReasonCode;
  /**
   * Untrusted values from the problem body (proposal, to be validated: C2 spec §5.4). Only
   * `reasonMessage` reads them, and it keeps only the names declared for the code.
   */
  readonly params: Problem['params'];
  /**
   * True only when the request got no response at all (fetch itself failed: offline, DNS, TLS).
   * Screens read it for their Offline state (M7 §4), never `status === 0`: a session failure or a
   * bug also has status 0 but has a reason to show.
   */
  readonly network: boolean;

  constructor(
    status: number,
    reasonCode: ReasonCode,
    params?: Problem['params'],
    options: { readonly network?: boolean } = {},
  ) {
    // The message is the code only, for logs: nothing from the response body is kept.
    super(reasonCode);
    this.name = 'ApiError';
    this.status = status;
    this.reasonCode = reasonCode;
    this.params = params;
    this.network = options.network ?? false;
  }
}

/** Anything that is not already an {@link ApiError} is a bug, shown with the generic text (I12). */
export function toApiError(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, 'INTERNAL_ERROR');
}

/** Options of {@link createApiClient}. */
export interface ApiClientOptions {
  /** API origin from the runtime config (`/config.js`), e.g. `https://api.fxmate.example`. */
  readonly baseUrl: string;
  /**
   * Current access token, sent as `Authorization: Bearer` on authenticated calls. The session
   * (S2) feeds it; it is read again for every attempt, so a replay after a renewal sends the new
   * token.
   */
  readonly accessToken?: (() => string | undefined) | undefined;
  /**
   * Gets a new access token after a 401 `TOKEN_EXPIRED` of an authenticated call (S2 R18). It
   * receives the token that call sent, so the session can skip a renewal another request already
   * did (AC36). It resolves when a new token is available and rejects with the {@link ApiError}
   * of the exchange. Absent: the 401 surfaces as is.
   */
  readonly renewToken?: ((staleToken: string | undefined) => Promise<void>) | undefined;
  /**
   * Called once when an authenticated call ends with a 401 `UNAUTHENTICATED` or `TOKEN_EXPIRED`
   * that no renewal can fix (spec §4 "Session ended"). The session turns it into its `failed` state.
   */
  readonly onSessionEnded?: ((error: ApiError) => void) | undefined;
  readonly fetch?: typeof fetch | undefined;
}

/**
 * How a write is authorised and de-duplicated (master X1, X3).
 *
 * - `authenticated`: Bearer token plus the caller's `idempotencyKey` (a UUID v4 made once per user
 *   action with `newIdempotencyKey()` and reused on every retry, S2 R13).
 * - `public`: neither. Only `POST /auth/telegram` is public (S2 R8, R14).
 */
type WriteGuard =
  { readonly kind: 'authenticated'; readonly idempotencyKey: string } | { readonly kind: 'public' };

/** What a write needs besides its path and the response schema. */
export interface WriteRequest {
  readonly guard: WriteGuard;
  /** JSON body, already typed by its `src/api` request schema; omit it for a write without one. */
  readonly body?: unknown;
}

/** The Mini App's only way to reach the API (CLAUDE.md: front end → API over HTTPS only). */
export interface ApiClient {
  /**
   * GETs `path` and parses the JSON body with the contract `schema`.
   *
   * @throws {ApiError} on a problem response, a network failure or a body that breaks the contract.
   */
  get<T>(path: string, schema: z.ZodType<T>, signal?: AbortSignal): Promise<T>;
  /**
   * POSTs `request.body` to `path` and parses the answer with `schema` (`z.void()` for a 204).
   *
   * `signal` aborts the request; the abort rejects unchanged, as in {@link ApiClient.get}. Only a
   * POST that writes nothing may pass one (the E7 risk preview, M2 R4): aborting a real write
   * would leave the trader unsure whether it happened. `put` and `delete` take none on purpose.
   *
   * @throws {ApiError} as {@link ApiClient.get}.
   * @throws {TypeError} when an authenticated write has an `idempotencyKey` that is not a UUID v4.
   */
  post<T>(
    path: string,
    request: WriteRequest,
    schema: z.ZodType<T>,
    signal?: AbortSignal,
  ): Promise<T>;
  /**
   * GETs a JPEG with the bearer token (S5 R14: an `<img>` cannot send it). Same renewal and
   * failures as {@link ApiClient.get}; a success that is not `image/jpeg` is `INTERNAL_ERROR`.
   *
   * @throws {ApiError} on a problem response, a network failure or a body that is not a JPEG.
   */
  getBlob(path: string, signal?: AbortSignal): Promise<Blob>;
  /** PUTs like {@link ApiClient.post}. */
  put<T>(path: string, request: WriteRequest, schema: z.ZodType<T>): Promise<T>;
  /** DELETEs like {@link ApiClient.post}; most deletes answer 204, so pass `z.void()`. */
  delete<T>(path: string, request: WriteRequest, schema: z.ZodType<T>): Promise<T>;
}

type WriteMethod = 'POST' | 'PUT' | 'DELETE';

/** Reads a successful response: the parsed value, or `null` when it breaks the contract. */
type ReadBody<T> = (response: Response) => Promise<{ readonly value: T } | null>;

function readWith<T>(schema: z.ZodType<T>): ReadBody<T> {
  return async (response) => {
    const parsed = schema.safeParse(await readJson(response));
    return parsed.success ? { value: parsed.data } : null;
  };
}

// S5 R14: only a JPEG is ever turned into an object URL.
const readJpeg: ReadBody<Blob> = async (response) => {
  const type = response.headers.get('content-type') ?? '';
  return type.startsWith('image/jpeg') ? { value: await response.blob() } : null;
};

/** One call, described once so an attempt and its replay are identical. */
interface Call<T> {
  readonly method: 'GET' | WriteMethod;
  readonly path: string;
  readonly read: ReadBody<T>;
  readonly accept: 'application/json' | 'image/jpeg';
  readonly auth: 'bearer' | 'none';
  readonly idempotencyKey?: string | undefined;
  readonly body?: unknown;
  readonly signal?: AbortSignal | undefined;
}

type Attempt<T> =
  | { readonly kind: 'ok'; readonly value: T }
  | {
      readonly kind: 'failed';
      readonly error: ApiError;
      /** The token this attempt sent; the renewal uses it to tell "expired" from "already renewed". */
      readonly sentToken: string | undefined;
    };

/**
 * Thin typed `fetch` wrapper (master §5.1 "API client"): every response is parsed with its
 * `src/api` schema and every failure becomes an {@link ApiError}.
 */
export function createApiClient(options: ApiClientOptions): ApiClient {
  const baseUrl = options.baseUrl.replace(/\/+$/, '');
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);

  async function attempt<T>(call: Call<T>): Promise<Attempt<T>> {
    const sentToken = call.auth === 'bearer' ? options.accessToken?.() : undefined;
    const headers = new Headers({ accept: call.accept });
    if (call.body !== undefined) headers.set('content-type', 'application/json');
    if (sentToken !== undefined) headers.set('authorization', `Bearer ${sentToken}`);
    if (call.idempotencyKey !== undefined) headers.set(IDEMPOTENCY_KEY_HEADER, call.idempotencyKey);

    let response: Response;
    try {
      response = await doFetch(`${baseUrl}${call.path}`, {
        method: call.method,
        headers,
        ...(call.body === undefined ? {} : { body: JSON.stringify(call.body) }),
        ...(call.signal === undefined ? {} : { signal: call.signal }),
      });
    } catch (error) {
      if (call.signal?.aborted === true) throw error;
      // No reason code exists yet for "network unreachable" (see the M1 spec Changes row);
      // the generic text is shown.
      return {
        kind: 'failed',
        error: new ApiError(0, 'INTERNAL_ERROR', undefined, { network: true }),
        sentToken,
      };
    }

    if (!response.ok) {
      const problem = parseProblem(await readJson(response), response.status);
      return {
        kind: 'failed',
        error: new ApiError(response.status, problem.reasonCode, problem.params),
        sentToken,
      };
    }
    const read = await call.read(response);
    // A body that breaks the contract is never rendered (S2 AC25).
    if (read === null) {
      return { kind: 'failed', error: new ApiError(response.status, 'INTERNAL_ERROR'), sentToken };
    }
    return { kind: 'ok', value: read.value };
  }

  async function execute<T>(call: Call<T>): Promise<T> {
    const first = await attempt(call);
    if (first.kind === 'ok') return first.value;
    const renew = options.renewToken;
    // S2 R18: only an expired token is renewed, and only for authenticated calls.
    if (renew === undefined || !isExpiredToken(call.auth, first.error)) {
      return giveUp(call.auth, first.error);
    }
    try {
      await renew(first.sentToken);
    } catch (error) {
      // The exchange failed: the session already records why (INIT_DATA_EXPIRED after 24 h, …).
      throw toApiError(error);
    }
    // One replay: same body, same Idempotency-Key (R13), and the token getter is read again.
    const second = await attempt(call);
    if (second.kind === 'ok') return second.value;
    return giveUp(call.auth, second.error);
  }

  function giveUp(auth: Call<unknown>['auth'], error: ApiError): never {
    // Spec §4 "Session ended": a 401 that no renewal can fix ends the session for the whole app.
    if (
      auth === 'bearer' &&
      error.status === 401 &&
      (error.reasonCode === 'UNAUTHENTICATED' || error.reasonCode === 'TOKEN_EXPIRED')
    ) {
      options.onSessionEnded?.(error);
    }
    throw error;
  }

  async function write<T>(
    method: WriteMethod,
    path: string,
    request: WriteRequest,
    schema: z.ZodType<T>,
    signal?: AbortSignal,
  ): Promise<T> {
    const authenticated = request.guard.kind === 'authenticated';
    // X3: an authenticated write always carries the caller's key; the public exchange carries none.
    const idempotencyKey =
      request.guard.kind === 'authenticated'
        ? requireUuidV4(request.guard.idempotencyKey)
        : undefined;
    const value = await execute({
      method,
      path,
      read: readWith(schema),
      accept: 'application/json',
      auth: authenticated ? 'bearer' : 'none',
      idempotencyKey,
      body: request.body,
      signal,
    });
    return value;
  }

  return {
    get<T>(path: string, schema: z.ZodType<T>, signal?: AbortSignal): Promise<T> {
      return execute({
        method: 'GET',
        path,
        read: readWith(schema),
        accept: 'application/json',
        auth: 'bearer',
        signal,
      });
    },
    getBlob(path: string, signal?: AbortSignal): Promise<Blob> {
      return execute({
        method: 'GET',
        path,
        read: readJpeg,
        accept: 'image/jpeg',
        auth: 'bearer',
        signal,
      });
    },
    post<T>(
      path: string,
      request: WriteRequest,
      schema: z.ZodType<T>,
      signal?: AbortSignal,
    ): Promise<T> {
      return write('POST', path, request, schema, signal);
    },
    put<T>(path: string, request: WriteRequest, schema: z.ZodType<T>): Promise<T> {
      return write('PUT', path, request, schema);
    },
    delete<T>(path: string, request: WriteRequest, schema: z.ZodType<T>): Promise<T> {
      return write('DELETE', path, request, schema);
    },
  };
}

/** X3, R13: the API refuses anything else with a 400; failing here points at the caller's bug. */
function requireUuidV4(key: string): string {
  if (!IdempotencyKeySchema.safeParse(key).success) {
    throw new TypeError('idempotencyKey must be a UUID v4');
  }
  return key;
}

function isExpiredToken(auth: Call<unknown>['auth'], error: ApiError): boolean {
  return auth === 'bearer' && error.status === 401 && error.reasonCode === 'TOKEN_EXPIRED';
}

async function readJson(response: Response): Promise<unknown> {
  try {
    const body: unknown = await response.json();
    return body;
  } catch {
    return undefined;
  }
}
